import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  PlusIcon,
  CalendarIcon,
  TrophyIcon,
  BellIcon,
  ChatBubbleLeftRightIcon,
  EyeIcon,
  HandThumbUpIcon,
  PencilIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PaperAirplaneIcon,
  MapPinIcon,
  BookmarkIcon,
  AtSymbolIcon,
  StarIcon,
  TrashIcon,
  EllipsisHorizontalIcon
} from '@heroicons/react/24/outline';
import { HandThumbUpIcon as HandThumbUpSolidIcon, MapPinIcon as MapPinSolidIcon, StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import { toast } from 'react-hot-toast';
import intranetApi from '../../services/intranetApi';
import notificationApi from '../../services/notificationApi';
import api from '../../utils/api';
import EditPost from './EditPost';
import PostLikes from './PostLikes';
import PostBookmark from './PostBookmark';
import MentionModal from '../common/MentionModal';
import { renderContentWithMentions } from '../../utils/mentions';
import { getIconComponent } from '../../utils/iconMapping';
import EditComment from './EditComment';

const CompanyFeed = () => {
  const [posts, setPosts] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  
  // Mention counts state
  const [mentionCounts, setMentionCounts] = useState({
    user_mentions: 0,
    department_mentions: 0,
    total_mentions: 0
  });
  
  // Required reading count state
  const [requiredReadingCount, setRequiredReadingCount] = useState(0);
  
  // Comment-related state
  const [expandedComments, setExpandedComments] = useState(new Set());
  const [postComments, setPostComments] = useState({});
  const [loadingComments, setLoadingComments] = useState(new Set());
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [activeCommentPostId, setActiveCommentPostId] = useState(null);

  // Track which posts have had their views tracked to avoid duplicate tracking
  const [trackedViews, setTrackedViews] = useState(new Set());
  const [expandedPosts, setExpandedPosts] = useState(new Set());

  const [showEditCommentModal, setShowEditCommentModal] = useState(false);
  const [editingComment, setEditingComment] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch recent posts (limit to 5 for the feed)
        const postsResponse = await intranetApi.posts.getPosts({
          limit: 5,
          offset: 0
        });

        // Fetch upcoming events (limit to 3 for sidebar)
        const eventsResponse = await intranetApi.events.getUpcomingEvents({
          limit: 3
        });

        setPosts(postsResponse.data.posts || []);
        setUpcomingEvents(eventsResponse.data.events || []);
      } catch (err) {
        console.error('Error fetching feed data:', err);
        setError('Failed to load company feed. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    const fetchCurrentUser = async () => {
      try {
        const userResponse = await api.get('/auth/me');
        setCurrentUser(userResponse.data.user);
      } catch (error) {
        console.error('Error fetching current user:', error);
      }
    };

    const fetchMentionCounts = async () => {
      try {
        const response = await notificationApi.getMentionCounts();
        setMentionCounts(response.counts);
      } catch (error) {
        console.error('Error fetching mention counts:', error);
      }
    };

    const fetchRequiredReadingCount = async () => {
      try {
        const response = await intranetApi.posts.getRequiredReadingCount();
        setRequiredReadingCount(response.data.count);
      } catch (error) {
        console.error('Error fetching required reading count:', error);
      }
    };

    fetchData();
    fetchCurrentUser();
    fetchMentionCounts();
    fetchRequiredReadingCount();
    
    // Set up interval to refresh mention counts every 30 seconds
    const mentionInterval = setInterval(fetchMentionCounts, 30000);
    
    // Set up interval to refresh required reading count every 30 seconds
    const requiredReadingInterval = setInterval(fetchRequiredReadingCount, 30000);
    
    // Refresh mention counts when user returns to the page
    const handleFocus = () => {
      fetchMentionCounts();
      fetchRequiredReadingCount();
    };
    
    // Refresh mention counts when user navigates back to this page
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchMentionCounts();
        fetchRequiredReadingCount();
      }
    };
    
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      clearInterval(mentionInterval);
      clearInterval(requiredReadingInterval);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Auto-fetch comments for posts with best answers
  useEffect(() => {
    const fetchBestAnswerComments = async () => {
      for (const post of posts) {
        if (post.has_best_answer && !postComments[post.id] && !loadingComments.has(post.id)) {
          await fetchComments(post.id);
        }
      }
    };

    if (posts.length > 0) {
      fetchBestAnswerComments();
    }
  }, [posts, postComments, loadingComments]);

  const trackPostView = async (postId) => {
    if (trackedViews.has(postId)) return;
    
    try {
      // Call the individual post endpoint to trigger view tracking
      await intranetApi.posts.getPost(postId);
      setTrackedViews(prev => new Set([...prev, postId]));
      
      // Update the post's view count in the UI
      setPosts(prev => prev.map(post => 
        post.id === postId 
          ? { ...post, view_count: (post.view_count || 0) + 1 }
          : post
      ));
    } catch (error) {
      // Silently fail - view tracking shouldn't break the UI
      console.error('Error tracking post view:', error);
    }
  };

  const handleReadMore = (postId) => {
    // Track view when user shows genuine interest by clicking "Read more"
    trackPostView(postId);
    
    // Expand the post content
    setExpandedPosts(prev => new Set([...prev, postId]));
  };

  const formatDate = (dateString) => {
    // FIXED: Parse datetime string directly to avoid timezone conversion
    // Same pattern as other components - don't create Date from ISO string
    if (!dateString) return '';
    
    try {
      // Extract date and time portions from datetime string
      let datePart, timePart;
      if (dateString.includes('T')) {
        [datePart, timePart] = dateString.split('T');
      } else if (dateString.includes(' ')) {
        [datePart, timePart] = dateString.split(' ');
      } else {
        return dateString; // Assume it's already formatted
      }
      
      // Parse date components and create Date object in local timezone
      const [year, month, day] = datePart.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error, dateString);
      return dateString;
    }
  };

  // Helper function to check if event spans multiple days
  const isMultiDayEvent = (startDateTime, endDateTime) => {
    if (!startDateTime || !endDateTime) return false;
    
    try {
      // Extract date portions
      const startDate = startDateTime.includes('T') 
        ? startDateTime.split('T')[0] 
        : startDateTime.split(' ')[0];
      const endDate = endDateTime.includes('T') 
        ? endDateTime.split('T')[0] 
        : endDateTime.split(' ')[0];
      
      return startDate !== endDate;
    } catch (error) {
      console.error('Error checking multi-day event:', error);
      return false;
    }
  };

  // Helper function to format date range for multi-day events
  const formatEventDateRange = (startDateTime, endDateTime) => {
    if (!startDateTime) return '';
    
    try {
      const startFormatted = formatDate(startDateTime);
      
      if (!endDateTime || !isMultiDayEvent(startDateTime, endDateTime)) {
        return startFormatted;
      }
      
      const endFormatted = formatDate(endDateTime);
      return `${startFormatted} - ${endFormatted}`;
    } catch (error) {
      console.error('Error formatting event date range:', error);
      return formatDate(startDateTime);
    }
  };

  // Comment-related functions
  const toggleComments = async (postId) => {
    const newExpanded = new Set(expandedComments);
    
    if (newExpanded.has(postId)) {
      // Collapse comments
      newExpanded.delete(postId);
      setExpandedComments(newExpanded);
    } else {
      // Expand comments and fetch if not already loaded
      newExpanded.add(postId);
      setExpandedComments(newExpanded);
      
      if (!postComments[postId]) {
        await fetchComments(postId);
      }
    }
  };

  const fetchComments = async (postId) => {
    const newLoadingComments = new Set(loadingComments);
    newLoadingComments.add(postId);
    setLoadingComments(newLoadingComments);

    try {
      const response = await intranetApi.comments.getComments(postId);
      setPostComments(prev => ({
        ...prev,
        [postId]: response.data.comments || []
      }));
    } catch (error) {
      console.error('Error fetching comments:', error);
      toast.error('Failed to load comments');
    } finally {
      newLoadingComments.delete(postId);
      setLoadingComments(newLoadingComments);
    }
  };

  const openCommentModal = (postId) => {
    setActiveCommentPostId(postId);
    setShowCommentModal(true);
  };

  const closeCommentModal = () => {
    setShowCommentModal(false);
    setActiveCommentPostId(null);
  };

  const submitComment = async (content) => {
    if (!activeCommentPostId) return;

    try {
      const response = await intranetApi.comments.addComment(activeCommentPostId, { content });
      
      if (response.data.success) {
        // Add the new comment to the list
        setPostComments(prev => ({
          ...prev,
          [activeCommentPostId]: [response.data.comment, ...(prev[activeCommentPostId] || [])]
        }));
        
        // Update the post's comment count
        setPosts(prev => prev.map(post => 
          post.id === activeCommentPostId 
            ? { ...post, comment_count: (post.comment_count || 0) + 1 }
            : post
        ));
        
        // Expand comments if not already expanded
        if (!expandedComments.has(activeCommentPostId)) {
          setExpandedComments(prev => new Set([...prev, activeCommentPostId]));
        }
        
        toast.success('Comment added successfully!');
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
      throw error; // Re-throw to let modal handle the error state
    }
  };

  const formatCommentDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  const handleEditPost = (post) => {
    setEditingPost(post);
    setShowEditModal(true);
  };

  const handlePostUpdated = (updatedPost) => {
    setPosts(prev => prev.map(post => 
      post.id === updatedPost.id ? updatedPost : post
    ));
    setShowEditModal(false);
    setEditingPost(null);
  };

  const canUserEditPost = (post) => {
    return currentUser && post.author_id === currentUser.id;
  };

  const isUserAdmin = () => {
    return currentUser && currentUser.role_type === 'admin';
  };

  const handleTogglePin = async (postId, currentPinStatus) => {
    if (!isUserAdmin()) {
      toast.error('Only administrators can pin posts');
      return;
    }

    try {
      const response = await intranetApi.posts.togglePin(postId);
      
      if (response.data.success) {
        // Update the post in the list
        setPosts(prev => prev.map(post => 
          post.id === postId 
            ? { ...post, is_pinned: response.data.is_pinned }
            : post
        ));
        
        toast.success(response.data.message);
      } else {
        throw new Error(response.data.error || 'Failed to toggle pin status');
      }
    } catch (error) {
      console.error('Error toggling pin:', error);
      toast.error(error.response?.data?.error || 'Failed to toggle pin status');
    }
  };

  const handleToggleLike = async (postId, currentLikeStatus) => {
    try {
      const response = await intranetApi.posts.toggleLike(postId);
      
      if (response.data.success) {
        // Update the post in the list
        setPosts(prev => prev.map(post => 
          post.id === postId 
            ? { 
                ...post, 
                user_liked: response.data.user_liked,
                like_count: response.data.like_count 
              }
            : post
        ));
        
        // Optional: Show toast for feedback
        // toast.success(response.data.user_liked ? 'Post liked!' : 'Like removed');
      } else {
        throw new Error(response.data.error || 'Failed to toggle like');
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      toast.error(error.response?.data?.error || 'Failed to toggle like');
    }
  };

  const handleToggleBookmark = async (postId, isBookmarked, bookmarkCount) => {
    // Update the post in the list with the new bookmark state
    setPosts(prev => prev.map(post => 
      post.id === postId 
        ? { 
            ...post, 
            user_bookmarked: isBookmarked,
            bookmark_count: bookmarkCount 
          }
        : post
    ));
  };

  const handleMarkBestAnswer = async (postId, commentId) => {
    try {
      const response = await intranetApi.comments.markAsBestAnswer(postId, commentId);
      
      if (response.data.success) {
        // Update the comments to reflect the new best answer
        setPostComments(prev => ({
          ...prev,
          [postId]: (prev[postId] || []).map(comment => ({
            ...comment,
            is_best_answer: comment.id === commentId
          }))
        }));
        
        // Update the post to show it has a best answer
        setPosts(prev => prev.map(post => 
          post.id === postId 
            ? { ...post, has_best_answer: true, best_answer_comment_id: commentId }
            : post
        ));
        
        toast.success('Comment marked as best answer!');
      }
    } catch (error) {
      console.error('Error marking best answer:', error);
      toast.error(error.response?.data?.error || 'Failed to mark best answer');
    }
  };

  const handleRemoveBestAnswer = async (postId) => {
    try {
      const response = await intranetApi.comments.removeBestAnswer(postId);
      
      if (response.data.success) {
        // Update the comments to remove best answer status
        setPostComments(prev => ({
          ...prev,
          [postId]: (prev[postId] || []).map(comment => ({
            ...comment,
            is_best_answer: false
          }))
        }));
        
        // Update the post to show it no longer has a best answer
        setPosts(prev => prev.map(post => 
          post.id === postId 
            ? { ...post, has_best_answer: false, best_answer_comment_id: null }
            : post
        ));
        
        toast.success('Best answer removed!');
      }
    } catch (error) {
      console.error('Error removing best answer:', error);
      toast.error(error.response?.data?.error || 'Failed to remove best answer');
    }
  };

  const canUserMarkBestAnswer = (post) => {
    if (!currentUser) return false;
    // Post author or admin can mark best answer
    return currentUser.id === post.user_id || currentUser.role_type === 'admin';
  };

  // Admin delete functionality
  const handleAdminDeletePost = async (postId) => {
    const reason = prompt('Please provide a reason for deleting this post:');
    if (!reason || reason.trim().length === 0) {
      toast.error('Deletion reason is required');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this post? This action cannot be undone.')) {
      return;
    }

    try {
      await intranetApi.posts.adminDeletePost(postId, reason.trim());
      toast.success('Post deleted successfully');
      
      // Remove the post from the UI
      setPosts(prev => prev.filter(post => post.id !== postId));
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error('Failed to delete post: ' + (error.response?.data?.error || error.message));
    }
  };

  // Admin delete comment functionality
  const handleAdminDeleteComment = async (commentId, postId) => {
    const reason = prompt('Please provide a reason for deleting this comment:');
    if (!reason || reason.trim().length === 0) {
      toast.error('Deletion reason is required');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this comment? This action cannot be undone.')) {
      return;
    }

    try {
      await intranetApi.comments.adminDeleteComment(commentId, reason.trim());
      toast.success('Comment deleted successfully');
      
      // Remove the comment from the UI
      setPostComments(prev => ({
        ...prev,
        [postId]: (prev[postId] || []).filter(comment => comment.id !== commentId)
      }));

      // Update the post's comment count
      setPosts(prev => prev.map(post => 
        post.id === postId 
          ? { ...post, comment_count: Math.max(0, (post.comment_count || 0) - 1) }
          : post
      ));
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast.error('Failed to delete comment: ' + (error.response?.data?.error || error.message));
    }
  };

  const canUserDeletePost = (post) => {
    if (!currentUser) return false;
    // Only admins can delete posts
    return currentUser.role_type === 'admin';
  };

  const canUserDeleteComment = () => {
    if (!currentUser) return false;
    // Only admins can delete comments
    return currentUser.role_type === 'admin';
  };

  const canUserEditComment = (comment) => {
    if (!currentUser) return false;
    // User can edit their own comments
    return currentUser.id === comment.author_id;
  };

  const handleEditComment = (comment) => {
    setEditingComment(comment);
    setShowEditCommentModal(true);
  };

  const handleCommentUpdated = (updatedComment) => {
    // Find which post this comment belongs to
    let targetPostId = null;
    for (const [postId, comments] of Object.entries(postComments)) {
      if (comments.some(comment => comment.id === updatedComment.id)) {
        targetPostId = postId;
        break;
      }
    }

    if (targetPostId) {
      setPostComments(prev => ({
        ...prev,
        [targetPostId]: (prev[targetPostId] || []).map(comment =>
          comment.id === updatedComment.id ? updatedComment : comment
        )
      }));
    }
    
    setShowEditCommentModal(false);
    setEditingComment(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-600 mb-4">{error}</p>
        <button 
          onClick={() => window.location.reload()} 
          className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors duration-200"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg shadow-lg p-8 text-white">
        <h2 className="text-2xl font-bold mb-2">Welcome to FrameWorX Company Page</h2>
        <p className="text-indigo-100 mb-6">
          Stay connected with company news, participate in events, and engage with your colleagues.
        </p>
        <div className="flex flex-wrap gap-4">
          <Link
            to="/company/posts"
            className="bg-white text-indigo-600 px-4 py-2 rounded-md font-medium hover:bg-indigo-50 transition-colors duration-200 flex items-center"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Create Post
          </Link>
          <Link
            to="/company/events"
            className="bg-indigo-400 text-white px-4 py-2 rounded-md font-medium hover:bg-indigo-300 transition-colors duration-200 flex items-center"
          >
            <CalendarIcon className="h-5 w-5 mr-2" />
            View Events
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Feed */}
        <div className="lg:col-span-2 space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-semibold text-gray-900">Recent Posts</h3>
            <Link
              to="/company/posts"
              className="text-indigo-600 hover:text-indigo-500 text-sm font-medium"
            >
              View All Posts →
            </Link>
          </div>

          {/* Posts */}
          {posts.length === 0 ? (
            <div className="bg-white rounded-lg shadow border border-gray-200 p-8 text-center">
              <p className="text-gray-500 mb-4">No posts yet. Be the first to share something!</p>
              <Link
                to="/company/posts"
                className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors duration-200 inline-flex items-center"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Create First Post
              </Link>
            </div>
          ) : (
            <div className="space-y-6">
              {posts.map((post) => (
                <div key={post.id} className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
                  {/* Post Header */}
                  <div className="p-6 pb-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">
                          <div className="h-10 w-10 rounded-full bg-indigo-500 flex items-center justify-center text-white font-medium">
                            {post.author_name ? post.author_name.charAt(0).toUpperCase() : 'U'}
                          </div>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{post.author_name || 'Unknown User'}</p>
                          <p className="text-xs text-gray-500">{formatDate(post.created_at)}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {post.has_best_answer && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            <StarSolidIcon className="h-3 w-3 mr-1" />
                            Best Answer
                          </span>
                        )}
                        {post.is_pinned && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            Pinned
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Category */}
                    {post.category_label && (
                      <div className="mt-3">
                        <span 
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white"
                          style={{ backgroundColor: post.category_color || '#6B7280' }}
                        >
                          {(() => {
                            const IconComponent = getIconComponent(post.category_icon, 'category');
                            return <IconComponent className="h-3 w-3 mr-1" />;
                          })()}
                          {post.category_label}
                        </span>
                      </div>
                    )}

                    {/* Post Content */}
                    <div className="mt-4">
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">{post.title}</h4>
                      <div className="text-gray-700 text-sm leading-relaxed">
                        {expandedPosts.has(post.id) ? (
                          <>
                            {renderContentWithMentions(post.content)}
                            <button 
                              onClick={() => setExpandedPosts(prev => {
                                const newSet = new Set(prev);
                                newSet.delete(post.id);
                                return newSet;
                              })}
                              className="text-indigo-600 hover:text-indigo-500 ml-1"
                            >
                              Show less
                            </button>
                          </>
                        ) : post.content.length > 200 ? (
                          <>
                            {renderContentWithMentions(post.content.substring(0, 200))}...
                            <button 
                              onClick={() => handleReadMore(post.id)}
                              className="text-indigo-600 hover:text-indigo-500 ml-1"
                            >
                              Read more
                            </button>
                          </>
                        ) : (
                          renderContentWithMentions(post.content)
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Post Actions */}
                  <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-6">
                        <div className="flex items-center text-gray-500 text-sm">
                          <EyeIcon className="h-4 w-4 mr-1" />
                          {post.view_count || 0}
                        </div>
                        <button 
                          onClick={() => toggleComments(post.id)}
                          className="flex items-center text-gray-500 hover:text-indigo-600 text-sm transition-colors duration-200"
                        >
                          <ChatBubbleLeftRightIcon className="h-4 w-4 mr-1" />
                          {post.comment_count || 0}
                          {expandedComments.has(post.id) ? (
                            <ChevronUpIcon className="h-4 w-4 ml-1" />
                          ) : (
                            <ChevronDownIcon className="h-4 w-4 ml-1" />
                          )}
                        </button>
                      </div>
                      <div className="flex items-center space-x-4">
                        <PostLikes
                          postId={post.id}
                          likeCount={post.like_count}
                          userLiked={post.user_liked}
                          onToggleLike={() => handleToggleLike(post.id, post.user_liked)}
                        />
                        <PostBookmark
                          postId={post.id}
                          bookmarkCount={post.bookmark_count}
                          userBookmarked={post.user_bookmarked}
                          onToggleBookmark={handleToggleBookmark}
                        />
                        {canUserEditPost(post) && (
                          <button 
                            onClick={() => handleEditPost(post)}
                            className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                            title="Edit post"
                          >
                            <PencilIcon className="h-5 w-5" />
                          </button>
                        )}
                        {isUserAdmin() && (
                          <button 
                            onClick={() => handleTogglePin(post.id, post.is_pinned)}
                            className={`transition-colors duration-200 ${
                              post.is_pinned 
                                ? 'text-yellow-600 hover:text-yellow-700' 
                                : 'text-gray-400 hover:text-yellow-600'
                            }`}
                            title={post.is_pinned ? 'Unpin post' : 'Pin post'}
                          >
                            <MapPinIcon className="h-5 w-5" />
                          </button>
                        )}
                        {canUserDeletePost(post) && (
                          <button 
                            onClick={() => handleAdminDeletePost(post.id)}
                            className="text-gray-400 hover:text-red-600 transition-colors duration-200"
                            title="Delete post"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Best Answer Section - Always visible when present */}
                  {post.has_best_answer && (
                    <div className="border-t border-gray-200 bg-yellow-50">
                      <div className="px-6 py-4">
                        <div className="flex items-center space-x-2 mb-3">
                          <StarSolidIcon className="h-5 w-5 text-yellow-600" />
                          <h4 className="text-sm font-semibold text-yellow-800">Best Answer</h4>
                        </div>
                        {postComments[post.id] ? (
                          postComments[post.id]
                            .filter(comment => comment.is_best_answer)
                            .map((comment) => (
                              <div key={comment.id} className="bg-white border border-yellow-200 rounded-lg p-4">
                                <div className="flex space-x-3">
                                  <div className="flex-shrink-0">
                                    <div className="h-8 w-8 rounded-full bg-yellow-500 flex items-center justify-center text-white text-sm font-medium">
                                      {comment.author_name ? comment.author_name.charAt(0).toUpperCase() : 'U'}
                                    </div>
                                  </div>
                                  <div className="flex-1">
                                    <div className="flex items-center justify-between mb-2">
                                      <div className="flex items-center space-x-2">
                                        <span className="text-sm font-medium text-gray-900">
                                          {comment.author_name || 'Unknown User'}
                                        </span>
                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                          <StarSolidIcon className="h-3 w-3 mr-1" />
                                          Best Answer
                                        </span>
                                      </div>
                                      <div className="flex items-center space-x-2">
                                        <span className="text-xs text-gray-500">
                                          {formatCommentDate(comment.created_at)}
                                        </span>
                                        {canUserEditComment(comment) && (
                                          <button
                                            onClick={() => handleEditComment(comment)}
                                            className="text-gray-400 hover:text-indigo-600 transition-colors duration-200"
                                            title="Edit comment"
                                          >
                                            <PencilIcon className="h-4 w-4" />
                                          </button>
                                        )}
                                        {canUserMarkBestAnswer(post) && (
                                          <button
                                            onClick={() => handleRemoveBestAnswer(post.id)}
                                            className="text-yellow-600 hover:text-yellow-700 transition-colors duration-200"
                                            title="Remove best answer"
                                          >
                                            <StarSolidIcon className="h-4 w-4" />
                                          </button>
                                        )}
                                        {canUserDeleteComment() && (
                                          <button
                                            onClick={() => handleAdminDeleteComment(comment.id, post.id)}
                                            className="text-gray-400 hover:text-red-600 transition-colors duration-200"
                                            title="Delete comment"
                                          >
                                            <TrashIcon className="h-4 w-4" />
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                    <p className="text-sm text-gray-700">{renderContentWithMentions(comment.content)}</p>
                                  </div>
                                </div>
                              </div>
                            ))
                        ) : (
                          <div className="bg-white border border-yellow-200 rounded-lg p-4">
                            <div className="flex justify-center py-2">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-600"></div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Comments Section */}
                  {expandedComments.has(post.id) && (
                    <div className="border-t-2 border-gray-100 bg-gray-50">
                      {/* Add Comment Form */}
                      <div className="px-6 py-4 bg-white border-b border-gray-200">
                        <div className="flex space-x-4">
                          <div className="flex-shrink-0">
                            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-gray-400 to-gray-600 flex items-center justify-center text-white text-sm font-semibold shadow-sm">
                              {currentUser?.name ? currentUser.name.charAt(0).toUpperCase() : 'U'}
                            </div>
                          </div>
                          <div className="flex-1">
                            <button
                              onClick={() => openCommentModal(post.id)}
                              className="w-full px-4 py-3 text-left text-gray-500 bg-gray-50 border border-gray-300 rounded-lg hover:bg-white hover:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 shadow-sm"
                            >
                              <span className="flex items-center">
                                <PaperAirplaneIcon className="h-4 w-4 mr-2 text-gray-400" />
                                Write a comment...
                              </span>
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Comments List */}
                      <div className="px-6 py-6 bg-gray-50 space-y-4">
                        {loadingComments.has(post.id) ? (
                          <div className="flex justify-center py-4">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {(postComments[post.id] || [])
                              .filter(comment => !comment.is_best_answer)
                              .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                              .map((comment) => (
                              <div key={comment.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow duration-200">
                                <div className="flex space-x-4">
                                  <div className="flex-shrink-0">
                                    <div className="h-10 w-10 rounded-full bg-gradient-to-br from-indigo-400 to-indigo-600 flex items-center justify-center text-white text-sm font-semibold shadow-sm">
                                      {comment.author_name ? comment.author_name.charAt(0).toUpperCase() : 'U'}
                                    </div>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between mb-3">
                                      <div className="flex items-center space-x-3">
                                        <span className="text-sm font-semibold text-gray-900">
                                          {comment.author_name || 'Unknown User'}
                                        </span>
                                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                                          {formatCommentDate(comment.created_at)}
                                        </span>
                                      </div>
                                      <div className="flex items-center space-x-2">
                                        {canUserEditComment(comment) && (
                                          <button
                                            onClick={() => handleEditComment(comment)}
                                            className="text-gray-400 hover:text-indigo-600 transition-colors duration-200 p-1 rounded-md hover:bg-gray-100"
                                            title="Edit comment"
                                          >
                                            <PencilIcon className="h-4 w-4" />
                                          </button>
                                        )}
                                        {canUserMarkBestAnswer(post) && (
                                          <button
                                            onClick={() => handleMarkBestAnswer(post.id, comment.id)}
                                            className="text-gray-400 hover:text-yellow-600 transition-colors duration-200 p-1 rounded-md hover:bg-yellow-50"
                                            title="Mark as best answer"
                                          >
                                            <StarIcon className="h-4 w-4" />
                                          </button>
                                        )}
                                        {canUserDeleteComment() && (
                                          <button
                                            onClick={() => handleAdminDeleteComment(comment.id, post.id)}
                                            className="text-gray-400 hover:text-red-600 transition-colors duration-200 p-1 rounded-md hover:bg-red-50"
                                            title="Delete comment"
                                          >
                                            <TrashIcon className="h-4 w-4" />
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                    <div className="prose prose-sm max-w-none">
                                      <p className="text-gray-700 leading-relaxed">{renderContentWithMentions(comment.content)}</p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                            
                            {(postComments[post.id] || []).filter(comment => !comment.is_best_answer).length === 0 && !loadingComments.has(post.id) && (
                              <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                                <ChatBubbleLeftRightIcon className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                                <p className="text-gray-500 text-sm font-medium">
                                  {post.has_best_answer ? 'No other comments yet.' : 'No comments yet. Be the first to comment!'}
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Upcoming Events */}
          <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-lg font-semibold text-gray-900">Upcoming Events</h4>
              <Link
                to="/company/events"
                className="text-indigo-600 hover:text-indigo-500 text-sm font-medium"
              >
                View All
              </Link>
            </div>
            {upcomingEvents.length === 0 ? (
              <p className="text-gray-500 text-sm">No upcoming events scheduled.</p>
            ) : (
              <div className="space-y-4">
                {upcomingEvents.map((event) => (
                  <div key={event.id} className="border-l-4 border-indigo-500 pl-4">
                    <h5 className="font-medium text-gray-900 text-sm">{event.title}</h5>
                    <p className="text-xs text-gray-500 mt-1">
                      {formatEventDateRange(event.start_datetime, event.end_datetime)}
                    </p>
                    {event.location && (
                      <p className="text-xs text-gray-500">{event.location}</p>
                    )}
                    {event.event_type_label && (
                      <span 
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-white mt-2"
                        style={{ backgroundColor: event.event_type_color || '#6B7280' }}
                      >
                        {event.event_type_label}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h4>
            <div className="space-y-3">
              <Link
                to="/company/posts"
                className="flex items-center p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors duration-200"
              >
                <PlusIcon className="h-5 w-5 text-indigo-500 mr-3" />
                <span className="text-sm font-medium text-gray-900">Create Post</span>
              </Link>
              <Link
                to="/company/mentions"
                className="flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors duration-200"
              >
                <div className="flex items-center">
                  <AtSymbolIcon className="h-5 w-5 text-indigo-500 mr-3" />
                  <span className="text-sm font-medium text-gray-900">Mentions</span>
                </div>
                <div className="transition-all duration-300 ease-in-out">
                  {(mentionCounts.user_mentions > 0 || mentionCounts.department_mentions > 0) && (
                    <span 
                      className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full min-w-[20px] text-center shadow-sm animate-pulse"
                      title={`${mentionCounts.user_mentions} personal mentions, ${mentionCounts.department_mentions} department mentions`}
                    >
                      {mentionCounts.user_mentions + mentionCounts.department_mentions}
                    </span>
                  )}
                </div>
              </Link>
              <Link
                to="/company/required-reading"
                className="flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors duration-200"
              >
                <div className="flex items-center">
                  <BellIcon className="h-5 w-5 text-red-500 mr-3" />
                  <span className="text-sm font-medium text-gray-900">Required Reading</span>
                </div>
                <div className="transition-all duration-300 ease-in-out">
                  {requiredReadingCount > 0 && (
                    <span 
                      className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full min-w-[20px] text-center shadow-sm animate-pulse"
                      title={`${requiredReadingCount} pending required reading post${requiredReadingCount !== 1 ? 's' : ''}`}
                    >
                      {requiredReadingCount}
                    </span>
                  )}
                </div>
              </Link>
              <Link
                to="/company/bookmarks"
                className="flex items-center p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors duration-200"
              >
                <BookmarkIcon className="h-5 w-5 text-indigo-500 mr-3" />
                <span className="text-sm font-medium text-gray-900">My Bookmarks</span>
              </Link>
              <Link
                to="/company/leaderboard"
                className="flex items-center p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors duration-200"
              >
                <TrophyIcon className="h-5 w-5 text-yellow-500 mr-3" />
                <span className="text-sm font-medium text-gray-900">View Leaderboard</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Post Modal */}
      <EditPost
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingPost(null);
        }}
        onPostUpdated={handlePostUpdated}
        post={editingPost}
      />

      {/* Comment Modal */}
      <MentionModal
        isOpen={showCommentModal}
        onClose={closeCommentModal}
        onSubmit={submitComment}
        title="Write a comment"
        placeholder="Share your thoughts... You can mention users with @username or departments with @departmentname"
        submitButtonText="Post Comment"
        maxLength={2000}
      />

      {/* Edit Comment Modal */}
      <EditComment
        isOpen={showEditCommentModal}
        onClose={() => {
          setShowEditCommentModal(false);
          setEditingComment(null);
        }}
        onCommentUpdated={handleCommentUpdated}
        comment={editingComment}
      />
    </div>
  );
};

export default CompanyFeed; 